import { Button } from "@progress/kendo-react-buttons";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { clipboardIcon } from "@progress/kendo-svg-icons";
import { useEffect, useMemo, useState, type ReactNode } from "react";

type ContentVisualizerCommonProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
};

type ContentVisualizerTextProps = ContentVisualizerCommonProps & {
    mode: "text";
    content: string | null | undefined;
};

type ContentVisualizerJsonProps = ContentVisualizerCommonProps & {
    mode: "json";
    content: unknown;
};

export type ContentVisualizerProps = ContentVisualizerTextProps | ContentVisualizerJsonProps;

type FormattedContent = {
    displayText: string;
    copyText: string | null;
    message: string | null;
};

type JsonTokenType = "property" | "string" | "number" | "boolean" | "null";

type JsonToken = {
    text: string;
    type?: JsonTokenType;
};

const noContentMessage = "No content to display";
const jsonTokenPattern = /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"\s*:)|("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false)\b|\b(null)\b/g;

const getSafeFallback = (content: unknown): string | null => {
    try {
        const fallback = String(content);
        return fallback === "[object Object]" ? null : fallback;
    } catch {
        return null;
    }
};

const formatTextContent = (content: string | null | undefined): FormattedContent => {
    if (content === null || content === undefined || content === "") {
        return {
            displayText: noContentMessage,
            copyText: null,
            message: null
        };
    }

    return {
        displayText: content,
        copyText: content,
        message: null
    };
};

const formatJsonContent = (content: unknown): FormattedContent => {
    if (content === undefined) {
        return {
            displayText: noContentMessage,
            copyText: null,
            message: null
        };
    }

    if (typeof content === "string") {
        try {
            const formatted = JSON.stringify(JSON.parse(content), null, 2);
            return {
                displayText: formatted,
                copyText: formatted,
                message: null
            };
        } catch {
            return {
                displayText: content,
                copyText: content,
                message: "Invalid JSON. Showing the original text."
            };
        }
    }

    try {
        const formatted = JSON.stringify(content, null, 2);
        if (formatted !== undefined) {
            return {
                displayText: formatted,
                copyText: formatted,
                message: null
            };
        }

        const fallback = getSafeFallback(content);
        return {
            displayText: fallback ?? "This value cannot be represented as JSON.",
            copyText: fallback,
            message: "Unable to serialize this value as JSON."
        };
    } catch (error: unknown) {
        const fallback = getSafeFallback(content);
        const reason = error instanceof Error ? error.message : "An unknown serialization error occurred.";
        return {
            displayText: fallback ?? "A safe text fallback is not available.",
            copyText: fallback,
            message: `Unable to serialize this value as JSON: ${reason}`
        };
    }
};

const tokenizeJson = (content: string): JsonToken[] => {
    const tokens: JsonToken[] = [];
    let previousIndex = 0;

    for (const match of content.matchAll(jsonTokenPattern)) {
        const index = match.index;
        if (index > previousIndex) {
            tokens.push({ text: content.slice(previousIndex, index) });
        }

        let type: JsonTokenType;
        if (match[1]) {
            type = "property";
        } else if (match[2]) {
            type = "string";
        } else if (match[3]) {
            type = "number";
        } else if (match[4]) {
            type = "boolean";
        } else {
            type = "null";
        }

        tokens.push({ text: match[0], type });
        previousIndex = index + match[0].length;
    }

    if (previousIndex < content.length) {
        tokens.push({ text: content.slice(previousIndex) });
    }

    return tokens;
};

const tokenClassNames: Record<JsonTokenType, string> = {
    property: "text-primary fw-semibold",
    string: "text-success",
    number: "text-info-emphasis",
    boolean: "text-warning-emphasis fw-semibold",
    null: "text-secondary fw-semibold"
};

const renderHighlightedJson = (content: string): ReactNode => tokenizeJson(content).map((token, index) => (
    token.type
        ? <span className={tokenClassNames[token.type]} key={index}>{token.text}</span>
        : token.text
));

const ContentVisualizer: React.FC<ContentVisualizerProps> = ({ isOpen, onClose, title = "Content", mode, content }) => {
    const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
    const formattedContent = useMemo(() => mode === "text"
        ? formatTextContent(content)
        : formatJsonContent(content), [content, mode]);

    useEffect(() => {
        setCopyFeedback(null);
    }, [content, isOpen, mode]);

    const handleCopyAsync = async (): Promise<void> => {
        if (formattedContent.copyText === null) {
            return;
        }

        try {
            if (!navigator.clipboard) {
                throw new Error("Clipboard access is unavailable.");
            }

            await navigator.clipboard.writeText(formattedContent.copyText);
            setCopyFeedback("Copied to clipboard.");
        } catch {
            setCopyFeedback("Unable to copy. Please copy the content manually.");
        }
    };

    if (!isOpen) {
        return null;
    }

    const hasHighlightedJson = mode === "json" && formattedContent.message === null && formattedContent.copyText !== null;
    let contentDisplay: ReactNode;

    if (mode === "text") {
        contentDisplay = (
            <div
                className={`p-3 lh-base ${formattedContent.copyText === null ? "text-body-secondary" : ""}`}
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
                {formattedContent.displayText}
            </div>
        );
    } else {
        contentDisplay = (
            <pre
                className={`m-0 p-3 small ${formattedContent.copyText === null ? "text-body-secondary" : ""}`}
                style={{ fontFamily: "var(--bs-font-monospace)", minWidth: "max-content", whiteSpace: "pre" }}
            >
                {hasHighlightedJson ? renderHighlightedJson(formattedContent.displayText) : formattedContent.displayText}
            </pre>
        );
    }

    return (
        <Window
            title={title}
            initialWidth={800}
            initialHeight={600}
            minWidth={300}
            minHeight={260}
            modal={true}
            onClose={onClose}
            style={{ maxWidth: "calc(100vw - 1rem)", maxHeight: "calc(100vh - 1rem)" }}
        >
            <div className="d-flex h-100 flex-column gap-2 overflow-hidden">
                {formattedContent.message && (
                    <div
                        className="small text-warning-emphasis"
                        role="alert"
                    >
                        {formattedContent.message}
                    </div>
                )}

                <div className="flex-grow-1 overflow-auto border rounded bg-body-tertiary">
                    {contentDisplay}
                </div>

                <div
                    className="small text-body-secondary"
                    style={{ minHeight: "1.25rem" }}
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {copyFeedback}
                </div>
            </div>

            <WindowActionsBar>
                <Button
                    type="button"
                    themeColor="primary"
                    svgIcon={clipboardIcon}
                    disabled={formattedContent.copyText === null}
                    onClick={() => void handleCopyAsync()}
                >
                    Copy
                </Button>
            </WindowActionsBar>
        </Window>
    );
};

export default ContentVisualizer;
